/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Custom Edit Guarantor button
 */

import _ from 'lodash';
import { Edit } from '@icon-park/react';
import { useEffect, useMemo, useState } from 'react';
import { Button, Modal, ButtonProps, App, Tooltip } from 'antd';
import { createForm } from '@formily/core';
import { Form } from '@formily/antd-v5';

import SchemaField from 'lib/formily/SchemaField';
import SubmitField from 'lib/formily/SubmitField';
import { NAVIGATION_PROMPT_MESSAGE } from 'components/page';
import getGuarantorSchema from 'components/forms/schema/guarantorSchema';
import useUpdateGuarantor from 'hooks/accounts/mutate/useUpdateGuarantor';
import { getInitialModel, prepareGuarantorUpdateModel } from 'utils/accounts/guarantor';

export interface IProps extends ButtonProps {
	parsedGuarantor: Guarantor.IParsedGuarantor;
	fhirGuarantor: Guarantor.IResource;
}

/**
 * AddInsurance button
 */
const EditGuarantor: React.FC<IProps> = ({ parsedGuarantor, fhirGuarantor, ...restProps }) => {
	const { modal } = App.useApp();
	const [open, setOpen] = useState(false);
	const form = useMemo(() => createForm(), [open]);
	const { mutate: updateGuarantor, isPending: isUpdating } = useUpdateGuarantor();
	const schema = useMemo(() => getGuarantorSchema(), [form]);
	const initialModel = useMemo(() => getInitialModel(parsedGuarantor), [form, parsedGuarantor]);

	useEffect(() => {
		form.setInitialValues(initialModel);
		form.setValues(_.cloneDeep(initialModel));
	}, [initialModel]);

	// handle reset
	const handleReset = () => {
		form?.reset();
	};

	// handle cancel
	const handleCancel = () => {
		if (_.isEqual(form.values, initialModel)) {
			setOpen(false);
		} else {
			modal.confirm({
				closable: true,
				title: 'Discard Changes?',
				content: NAVIGATION_PROMPT_MESSAGE,
				okText: 'Yes, discard',
				onOk: () => {
					setOpen(false);
				},
			});
		}
	};

	// handle submit
	const handleSubmit = (values: Guarantor.IFormModel) => {
		const submitModel = prepareGuarantorUpdateModel(values, fhirGuarantor);
		updateGuarantor(
			{ guarantorId: fhirGuarantor?.id, guarantorFhirModel: { fhirResource: submitModel } },
			{
				onSuccess: () => {
					handleReset();
					setOpen(false);
				},
			},
		);
	};

	return (
		<>
			<Tooltip title={'Edit Guarantor'}>
				<Button type='primary' icon={<Edit />} onClick={() => setOpen(true)} {...restProps} />
			</Tooltip>
			<Modal
				open={open}
				destroyOnHidden
				title={'Edit Guarantor'}
				closable={true}
				onCancel={handleCancel}
				footer={null}
				width={'50%'}
				className='tw-modal-form'
				bodyProps={{ style: { height: '50vh', minHeight: 550 } }}
				maskClosable={false}
			>
				<Form form={form} layout='vertical' key={form.id}>
					<SchemaField schema={schema} />
					<div className='tw-modal-form-footer'>
						<Button key='reset' onClick={handleReset} disabled={isUpdating}>
							{'Reset'}
						</Button>
						<SubmitField
							key={form.id}
							initialModel={initialModel}
							disabled={isUpdating}
							loading={isUpdating}
							onSubmit={handleSubmit}
						/>
					</div>
				</Form>
			</Modal>
		</>
	);
};

export default EditGuarantor;
